<script setup lang="ts">
import { computed } from 'vue';
import { VueDraggableNext } from 'vue-draggable-next';
import store from '@/store';

const item = computed({
  get: () => store.getters['sticker/GET_STICKER'],
  set: (value) => {
    store.dispatch('sticker/SET_STICKER', value);
  },
});

// Only show non-addon tiers; preserve isAddOn varients when saving
const addOnVarients = computed(() => (item.value.varients || []).filter((v: any) => v.isAddOn));

const mainTiers = computed(() =>
  (item.value.varients || [])
    .map((v: any, i: number) => ({ tier: v, origIdx: i }))
    .filter(({ tier }) => !tier.isAddOn),
);

const updateVariants = (updatedMain: any[]) => {
  store.dispatch('sticker/SET_STICKER', {
    varients: [...updatedMain, ...addOnVarients.value],
  });
};

// For draggable v-model binding - needs to maintain { tier, origIdx } structure
const draggableList = computed({
  get: () => mainTiers.value,
  set: (reordered) => {
    const reorderedTiers = reordered.map(({ tier }: any, index: number) => ({
      ...tier,
      sortOrder: index,
    }));
    updateVariants(reorderedTiers);
  },
});

const onAddTier = () => {
  const currentMain = mainTiers.value.map(({ tier }) => tier);
  currentMain.push({
    id: null,
    title: '',
    cost: 0,
    isFree: false,
    whatsIncluded: null,
    selectionOptions: [],
    isAddOn: false,
    quantity: null,
    sortOrder: currentMain.length,
    stickerId: null,
    description: null,
    isActive: true,
  });
  updateVariants(currentMain);
};

const onRemoveTier = (origIdx: number) => {
  const allVarients = [...(item.value.varients || [])];
  allVarients.splice(origIdx, 1);
  store.dispatch('sticker/SET_STICKER', { varients: allVarients });
};

// Helpers that operate on original varients indices
const updateTierField = (origIdx: number, patch: Record<string, any>) => {
  const varients = [...(item.value.varients || [])];
  varients[origIdx] = { ...varients[origIdx], ...patch };
  store.dispatch('sticker/SET_STICKER', { varients });
};

const onAddBullet = (origIdx: number) => {
  const current = (item.value.varients || [])[origIdx];
  const lines = current.whatsIncluded ? current.whatsIncluded.split('\n') : [];
  lines.push('');
  updateTierField(origIdx, { whatsIncluded: lines.join('\n') });
};

const onAddOption = (origIdx: number) => {
  const current = (item.value.varients || [])[origIdx];
  const options = Array.isArray(current.selectionOptions) ? [...current.selectionOptions] : [];
  options.push('');
  updateTierField(origIdx, { selectionOptions: options });
};

const onRemoveOption = (origIdx: number, optIndex: number) => {
  const current = (item.value.varients || [])[origIdx];
  const options = Array.isArray(current.selectionOptions) ? [...current.selectionOptions] : [];
  options.splice(optIndex, 1);
  updateTierField(origIdx, { selectionOptions: options });
};

const getBulletLines = (whatsIncluded: string | null): string[] => {
  if (whatsIncluded === null || whatsIncluded === undefined) return [];
  return whatsIncluded.split('\n');
};

const updateBulletLine = (origIdx: number, lineIndex: number, value: string) => {
  const current = (item.value.varients || [])[origIdx];
  const lines = current.whatsIncluded ? current.whatsIncluded.split('\n') : [];
  lines[lineIndex] = value;
  updateTierField(origIdx, { whatsIncluded: lines.join('\n') });
};

const removeBulletLine = (origIdx: number, lineIndex: number) => {
  const current = (item.value.varients || [])[origIdx];
  const lines = current.whatsIncluded ? current.whatsIncluded.split('\n') : [];
  lines.splice(lineIndex, 1);
  updateTierField(origIdx, { whatsIncluded: lines.join('\n') });
};

const onTypeChange = (origIdx: number, type: string) => {
  const current = (item.value.varients || [])[origIdx];
  const isFree = type === 'Free';
  updateTierField(origIdx, { isFree, cost: isFree ? 0 : current.cost });
};

const preventArrowKeys = (event: KeyboardEvent) => {
  if (event.key === 'ArrowUp' || event.key === 'ArrowDown') {
    event.preventDefault();
  }
};
</script>

<template lang="pug">
section.c-tiers
  vue-draggable-next(
    v-model="draggableList"
    handle=".c-tier__drag-handle"
  )
    article.c-tier(v-for="({ tier, origIdx }, i) in draggableList" :key="origIdx")
      .c-tier__header
        .c-tier__drag-handle ⠿
        .c-tier__badge {{ i + 1 }}
        span.c-tier__badge-label Tier {{ i + 1 }}
        fai.c-tier__delete-icon(
          type="fal"
          icon="trash"
          @click="onRemoveTier(origIdx)"
        )

      .c-tier__body
        .c-tier__row
          .c-tier__field
            span.c-tier__label Tier Title
            input.c-input(
              type="text"
              placeholder="e.g., General Admission, VIP, Early Bird"
              :value="tier.title"
              @input="(e) => updateTierField(origIdx, { title: e.target.value })"
            )
          .c-tier__field
            span.c-tier__label Type
            el-select(
              :model-value="tier.isFree ? 'Free' : 'Paid'"
              @update:model-value="(val) => onTypeChange(origIdx, val)"
              class="c-tier__type-select"
            )
              el-option(label="Paid" value="Paid")
              el-option(label="Free" value="Free")

        .c-tier__field.c-tier__field--full
          span.c-tier__label Tier Description
          textarea.c-input.c-input--textarea(
            placeholder="Details for this ticket tier"
            :value="tier.description"
            @input="(e) => updateTierField(origIdx, { description: e.target.value })"
          )

        .c-tier__row.c-tier__row--sections
          .c-tier__section
            .c-tier__section-header
              span.c-tier__label What's Included
              button.c-tier__add-link(type="button" @click="onAddBullet(origIdx)") + Add Bullet
            p.c-tier__hint Add bullet points to highlight what's included in this tier
            .c-tier__bullets(v-if="getBulletLines(tier.whatsIncluded).length")
              .c-tier__bullet-row(v-for="(line, li) in getBulletLines(tier.whatsIncluded)" :key="li")
                input.c-input(
                  type="text"
                  placeholder="Bullet point..."
                  :value="line"
                  @input="(e) => updateBulletLine(origIdx, li, e.target.value)"
                )
                button.c-tier__remove-bullet(type="button" @click="removeBulletLine(origIdx, li)") ×

          .c-tier__section
            .c-tier__section-header
              span.c-tier__label Selection Options (optional)
              button.c-tier__add-link(type="button" @click="onAddOption(origIdx)") + Add Option
            p.c-tier__hint Add choices like meal preferences, t-shirt sizes, etc.
            .c-tier__options(v-if="Array.isArray(tier.selectionOptions) && tier.selectionOptions.length")
              .c-tier__option-row(v-for="(opt, oi) in tier.selectionOptions" :key="oi")
                input.c-input(
                  type="text"
                  placeholder="Option..."
                  :value="opt"
                  @input="(e) => { const opts = [...(tier.selectionOptions || [])]; opts[oi] = e.target.value; updateTierField(origIdx, { selectionOptions: opts }); }"
                )
                button.c-tier__remove-bullet(type="button" @click="onRemoveOption(origIdx, oi)") ×

        .c-tier__row.c-tier__row--bottom
          .c-tier__field
            span.c-tier__label Max Quantity (optional)
            input.c-input(
              type="number"
              placeholder="100"
              min="0"
              :value="tier.quantity"
              @input="(e) => updateTierField(origIdx, { quantity: e.target.value ? parseInt(e.target.value) : null })"
              @keydown="preventArrowKeys"
              @wheel.prevent
            )
          .c-tier__field(v-if="!tier.isFree")
            span.c-tier__label Cost
            .c-tier__cost-input
              span.c-tier__cost-prefix $
              input.c-input.c-input--cost(
                type="number"
                placeholder="0.00"
                min="0"
                step="0.01"
                :value="tier.cost"
                @input="(e) => updateTierField(origIdx, { cost: parseFloat(e.target.value) || 0 })"
                @keydown="preventArrowKeys"
                @wheel.prevent
              )

  .c-tiers__add(@click="onAddTier")
    fai(type="fal" icon="plus-circle").size-16
    span + Add Ticket Tier
</template>

<style scoped lang="scss">
.c-tiers {
  display: flex;
  flex-direction: column;
  gap: 16px;

  &__add {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    width: 100%;
    height: 48px;
    border: 1px dashed #cccccc;
    border-radius: 8px;
    cursor: pointer;
    font-size: 14px;
    font-weight: 600;
    color: #333;
    font-family: 'Red Hat Display', sans-serif;
    margin-top: 8px;
    transition: border-color 0.2s, color 0.2s;

    &:hover {
      border-color: #1ebeaf;
      color: #1ebeaf;
    }
  }
}

.c-tier {
  border: 1px solid #e8e8e8;
  border-radius: 10px;
  overflow: hidden;
  background: #fff;
  margin-bottom: 16px;

  &__header {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 12px 16px;
    border-bottom: 1px solid #f0f0f0;
  }

  &__drag-handle {
    font-size: 18px;
    color: #aaaaaa;
    cursor: grab;
    line-height: 1;
  }

  &__badge {
    width: 24px;
    height: 24px;
    border-radius: 50%;
    background: #1ebeaf;
    color: #fff;
    font-size: 12px;
    font-weight: 700;
    display: flex;
    align-items: center;
    justify-content: center;
    font-family: 'Red Hat Display', sans-serif;
    flex-shrink: 0;
  }

  &__badge-label {
    font-size: 15px;
    font-weight: 600;
    font-family: 'Red Hat Display', sans-serif;
    flex: 1;
  }

  &__delete-icon {
    color: #e02020;
    cursor: pointer;
    font-size: 16px;
    width: 16px;
    height: 16px;
    flex-shrink: 0;
    opacity: 0.7;
    transition: opacity 0.2s;

    &:hover {
      opacity: 1;
    }
  }

  &__body {
    padding: 16px;
    display: flex;
    flex-direction: column;
    gap: 16px;
  }

  &__row {
    display: flex;
    gap: 16px;

    &--bottom {
      align-items: flex-end;
    }

    &--sections {
      align-items: flex-start;
      
      .c-tier__section {
        flex: 1;
      }
    }
  }

  &__field {
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 6px;

    &--full {
      flex: none;
      width: 100%;
    }
  }

  &__label {
    font-size: 13px;
    font-weight: 600;
    color: #333;
    font-family: 'Red Hat Display', sans-serif;
  }

  &__type-select {
    width: 100%;

    :deep(.el-select__wrapper),
    :deep(.el-input__wrapper) {
      --el-input-height: 56px;
      height: 56px !important;
      box-sizing: border-box;
      padding: 0 25px;
    }
  }

  &__section {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }

  &__section-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-right: 26px;
  }

  &__hint {
    font-size: 12px;
    color: #888;
    margin: 0;
    font-family: 'Red Hat Display', sans-serif;
  }

  &__add-link {
    background: none;
    border: 1px solid #1ebeaf;
    color: #1ebeaf;
    font-size: 12px;
    font-weight: 600;
    cursor: pointer;
    padding: 4px 10px;
    border-radius: 5px;
    font-family: 'Red Hat Display', sans-serif;
    transition: background 0.2s, color 0.2s;

    &:hover {
      background: #1ebeaf;
      color: #fff;
    }
  }

  &__bullets,
  &__options {
    display: flex;
    flex-direction: column;
    gap: 6px;
  }

  &__bullet-row,
  &__option-row {
    display: flex;
    align-items: center;
    gap: 8px;

    .c-input {
      flex: 1;
    }
  }

  &__remove-bullet {
    background: none;
    border: none;
    font-size: 18px;
    color: #aaa;
    cursor: pointer;
    line-height: 1;
    padding: 0 4px;
    transition: color 0.2s;

    &:hover {
      color: #e02020;
    }
  }

  &__cost-input {
    display: flex;
    align-items: center;
    border: 1px solid #ccc;
    border-radius: 5px;
    overflow: hidden;
    background: #fff;
  }

  &__cost-prefix {
    padding: 0 8px;
    font-size: 14px;
    color: #555;
    border-right: 1px solid #ddd;
    background: #f5f5f5;
    height: 56px;
    display: flex;
    align-items: center;
  }

  .c-input--cost {
    border: none;
    border-radius: 0;
    flex: 1;
  }

  .c-input {
    width: 100%;
    height: 56px;
    min-height: 56px;
    border: 1px solid #ccc;
    border-radius: 5px;
    padding: 20px 25px;
    font-size: 14px;
    font-weight: 500;
    font-family: 'Red Hat Display', sans-serif;
    outline: none;
    transition: border-color 0.2s;
    box-sizing: border-box;

    &:focus {
      border-color: #1ebeaf;
    }

    &--textarea {
      height: 80px;
      min-height: 80px;
      padding: 12px 25px;
      resize: vertical;
    }
  }

  input[type=number]::-webkit-outer-spin-button,
  input[type=number]::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }

  input[type=number] {
    -moz-appearance: textfield;
    appearance: textfield;
  }
}
</style>
